import { Data, UI, Utils } from 'common';
import InlineSpinner from 'common/src/code/ui/InlineSpinner.component';
import Link from 'common/src/code/ui/Link.component';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import moment from 'moment';
import React from 'react';
import { TrackingNumberType } from '../shared/TrackingNumberType.enum';
import HistoryCommunication from '../utils/HistoryCommunication';
import HistoryUtil from '../utils/Util';
import { DataDisplayPanel } from './DataDisplayPanel.component';
import TrackingSteppers from './TrackingSteppers.component';

const { WrappedButton, WrappedTooltip, WrappedButtonType } = UI;
const { Session, Util, Navigation } = Utils;
const { Capability, Carrier } = Data;

class TrackingDetails extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			carrier: null,
			trackingNumber: null,
			trackingNumberType: null,
			eta: null,
			isTrackingEventsVisible: false,
			isFetching: false,
			trackingEvents: [],
		};
	}

	componentDidMount() {
		this.updateState();
	}

	componentDidUpdate(prevProp) {
		const { selectedPrint } = this.props;

		if (selectedPrint !== prevProp.selectedPrint) {
			this.updateState();
		}
	}

	fetchTrackingDetails = async () => {
		const { selectedPrint } = this.props;
		const { carrier, trackingNumber, trackingNumberType } = this.state;

		if (
			isString(trackingNumber) &&
			isString(carrier) &&
			trackingNumberType === TrackingNumberType.Pic
		) {
			const response = await HistoryCommunication.getTrackingDetails({
				carrier,
				trackingNumber: trackingNumber.trim(),
			});

			if (response.status === 'Failure') {
				let {
					error: { errorMessage },
				} = response;

				// Remove extra text from error message:
				errorMessage = errorMessage.replace('Please check again later.', '');

				this.setState({
					isFetching: false,
					trackingErrorMessage: errorMessage,
				});
				return;
			}

			const newTrackingDetails = HistoryCommunication.isSuccessful(response)
				? Util.toCamelCase(response.data)
				: {};

			this.setState({
				isFetching: false,
				eta: response.data?.estimated_delivery_date,
				trackingEvents: this.buildTrackingEvents(
					selectedPrint,
					newTrackingDetails
				),
			});
		} else {
			this.setState({
				isFetching: false,
				trackingEvents: this.buildTrackingEvents(selectedPrint),
			});
		}
	};

	/**
	 * Build tracking history events.
	 * @param {object} selectedPrint
	 * @param {object} trackingDetails Tracking details received from the api.
	 * @returns {array}
	 */
	buildTrackingEvents = (selectedPrint, trackingDetails) => {
		const trackingEvents =
			isObject(trackingDetails) && isArray(trackingDetails.trackingEvents)
				? trackingDetails.trackingEvents
				: [];
		const allTrackingEvents = [];

		// build tracking events based on tracking api response
		if (trackingEvents.length > 0) {
			for (let idx = 0; idx < trackingEvents.length; idx++) {
				const {
					eventDescription,
					occuredAt,
					city,
					countryCode,
					stateProvince,
					postalCode,
				} = trackingEvents[idx];

				allTrackingEvents.push({
					eventDescription,
					occuredAt: HistoryUtil.convertGmtTimeToLocalTime(occuredAt),
					city,
					countryCode,
					stateProvince,
					postalCode,
				});
			}
		}

		// the initial event comes from `selectedPrint` print data.
		if (isObject(selectedPrint)) {
			const { transactionDate } = selectedPrint;
			const { fromAddress } = isObject(selectedPrint.printDetails)
				? selectedPrint.printDetails
				: {};

			allTrackingEvents.push({
				eventDescription: 'Printed',
				occuredAt: transactionDate,
				city: fromAddress.city,
				countryCode: fromAddress.countryCode,
				stateProvince: fromAddress.stateProvince,
				postalCode: fromAddress.postalCode,
			});
		}

		return allTrackingEvents;
	};

	/**
	 * Build the location text.
	 * @param {object} address
	 * @returns {string}
	 */
	buildLocationText = ({ city, countryCode, postalCode, stateProvince }) =>
		[
			city ? `${city},` : '',
			stateProvince || '',
			countryCode === 'US' ? postalCode : countryCode,
		]
			.filter(str => str)
			.join(' ');

	/**
	 * Store carrier and tracking number in the state object and fetch tracking events.
	 */
	updateState = () => {
		const { selectedPrint } = this.props;
		const { carrier, trackingNumber, trackingNumberType } = isObject(
			selectedPrint?.printDetails
		)
			? selectedPrint.printDetails
			: {};

		this.setState(
			{
				carrier,
				trackingNumber: isString(trackingNumber) ? trackingNumber.trim() : null,
				trackingNumberType,

				isFetching: true,
				trackingEvents: [],
			},
			this.fetchTrackingDetails
		);
	};

	onTrackingNumberClick = () => {
		const { isTrackingEventsVisible, carrier, trackingNumber } = this.state;

		this.setState({
			isTrackingEventsVisible: !isTrackingEventsVisible,
		});

		if (carrier === Carrier.FedEx) {
			Navigation.newTab(
				`https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
			);
		}
	};

	renderLastTrackingEvent() {
		const { trackingEvents } = this.state;

		if (trackingEvents.length > 0) {
			// events are sorted in chronically descending order, hence
			// the first event is the lastest event
			const lastTrackingEvent = trackingEvents[0];

			return (
				<div className="last-tracking-event">
					<p>
						{moment(lastTrackingEvent.occuredAt).format(
							'dddd, MMMM D, YYYY hh:mm A'
						)}
					</p>
					<p className="location">
						{this.buildLocationText(lastTrackingEvent)}
					</p>
				</div>
			);
		}

		return null;
	}

	renderETA() {
		const { eta } = this.state;
		return (
			eta && (
				<DataDisplayPanel
					className="eta"
					label="ETA:"
					body={moment(eta).format('MM/DD/YYYY')}
					dataqa="tracking-eta"
				/>
			)
		);
	}

	renderTrackingNumber() {
		const { selectedPrint, features, onEmailTrackingInfoClick } = this.props;
		const {
			trackingNumber,
			trackingNumberType,
			trackingEvents,
			isTrackingEventsVisible,
		} = this.state;

		if (trackingNumberType === TrackingNumberType.Pic) {
			return (
				<>
					<>
						<div className="tracking-number-details">
							Tracking:{' '}
							<Link
								className="tracking-number"
								data-qa="tracking-number"
								dataqa="tracking-number"
								onClick={this.onTrackingNumberClick}
							>
								{trackingNumber}
								<span
									className={`pi ${
										isTrackingEventsVisible ? 'pi-angle-down' : 'pi-angle-right'
									}`}
								/>
							</Link>
						</div>

						<WrappedTooltip
							target="tracking-number"
							className="tracking-number-tooltip"
							position="bottom"
						>
							<b>Show/Hide Tracking</b> <br />
							Show or hide the tracking details for this package.
						</WrappedTooltip>
					</>

					{isTrackingEventsVisible && (
						<TrackingSteppers
							className="tracking-events"
							selectedPrint={selectedPrint}
							trackingEvents={trackingEvents}
						/>
					)}
					{features.emailTrackingInfo.visible && (
						<div className="email-tracking-info-container">
							<Link
								disabled={features.emailTrackingInfo.disabled}
								className="email-tracking-info-button"
								onClick={onEmailTrackingInfoClick}
								dataqa="tracking-details-email-tracking-info-link"
							>
								Email Tracking Info
							</Link>
							<WrappedTooltip
								tooltip="<b>Email Tracking Info</b><br />Send an email with the tracking information for this package."
								target="email-tracking-info-button"
								position="bottom"
							/>
						</div>
					)}
				</>
			);
		}

		return null;
	}

	render() {
		const { isFetching, trackingErrorMessage } = this.state;
		const {
			selectedPrint,
			features,
			onClickCreateReturnLabelButton,
			onEmailTrackingInfoClick,
			onFileClaimClick,
			onCheckClaimStatusClick,
			onReprintClick,
		} = this.props;
		const { printStatusName, insuranceType } = isObject(
			selectedPrint?.printDetails
		)
			? selectedPrint.printDetails
			: {};
		const insuranceName =
			insuranceType === 'parcelguard'
				? 'ParcelGuard&trade;'
				: 'Stamps.com Insurance';
		const canPrintReturnLabel = Session.hasCapability(
			Capability.HistoryReturnLabel
		);
		const showUpgradeIconReturnLabel =
			!canPrintReturnLabel && !features.returnLabel.disabled;
		const sessionData = Session.getData();

		return (
			<div className="tracking-detail-panel">
				<div className="print-status-detail">
					<div className="print-status-name">{printStatusName}</div>

					{/* show the spinner while system is making a call to fetch tracking events */}
					{isFetching === true ? (
						<InlineSpinner />
					) : (
						this.renderLastTrackingEvent()
					)}
					{!isFetching ? this.renderETA() : ''}
					{this.renderTrackingNumber()}
					{trackingErrorMessage && <p>{trackingErrorMessage}</p>}
				</div>
				<div className="p-grid p-nogutter tracking-detail-menu-list">
					{features.returnLabel.visible && (
						<>
							<WrappedButton
								label="Create Return Label"
								className={`create-return-label-button ${
									showUpgradeIconReturnLabel ? 'upgrade-icon-btn' : ''
								}`}
								disabled={features.returnLabel.disabled}
								dataqa="common-buttongrid-btn"
								buttonType={WrappedButtonType.TertiaryGrey}
								additionalIconClassName={
									showUpgradeIconReturnLabel ? 'upgrade-icon' : null
								}
								icon={null}
								onClick={onClickCreateReturnLabelButton}
							/>
							<WrappedTooltip
								tooltip={
									showUpgradeIconReturnLabel
										? 'Upgrade your plan to Office to Create Return Label.'
										: '<b>Create Return Label</b><br />Create a return label for this package.'
								}
								target="create-return-label-button"
								position="top"
							/>
						</>
					)}
					{features.reprint.visible && (
						<>
							<WrappedButton
								label="Reprint"
								disabled={features.reprint.disabled}
								className="tracking-detail-menu-icon reprint-button"
								dataqa="details-panel-reprint-button"
								buttonType={WrappedButtonType.TertiaryGrey}
								icon={null}
								onClick={onReprintClick}
							/>
							{features.reprint.disabled && (
								<WrappedTooltip
									tooltip={features.reprint.ineligibleForReprintReason}
									target="reprint-button"
									position="top"
									data-qa="details-panel-reprint-ineligible-tooltip"
									dataqa="details-panel-reprint-ineligible-tooltip"
								/>
							)}
						</>
					)}
					{features.fileClaim.visible && (
						<>
							<WrappedButton
								label="File Claim"
								className="tracking-detail-menu-icon file-claims-button"
								dataqa="common-buttongrid-btn"
								buttonType={WrappedButtonType.TertiaryGrey}
								icon={null}
								disabled={features.fileClaim.disabled}
								onClick={onFileClaimClick}
							/>
							<WrappedTooltip
								tooltip={`<b>File Claim</b><br />File a ${insuranceName} claim for this print.`}
								target="file-claims-button"
								position="bottom"
							/>
						</>
					)}
					{features.checkClaimStatus.visible && (
						<>
							<WrappedButton
								label="Check Claim Status"
								className="tracking-detail-menu-icon check-claim-status-button"
								dataqa="common-buttongrid-btn"
								buttonType={WrappedButtonType.TertiaryGrey}
								icon={null}
								tooltipOptions={{
									position: 'bottom',
								}}
								onClick={onCheckClaimStatusClick}
							/>
							<WrappedTooltip
								tooltip="<b>Check Claim Status</b><br />If you have already filed an insurance claim, you can check the status of your claim with your claim number from your confirmation email."
								target="check-claim-status-button"
								position="bottom"
							/>
						</>
					)}
					{features.refundLateDelivery.visible && (
						<>
							<WrappedButton
								label="Refund Late Delivery"
								className="tracking-detail-menu-icon refund-late-delivery-button"
								dataqa="common-buttongrid-btn"
								buttonType={WrappedButtonType.TertiaryGrey}
								icon={null}
							/>
							<WrappedTooltip
								tooltip="<b>Refund Late Delivery</b><br />Submit refund for Priority Mail Express mail that did not arrive based on the guaranteed arrival time."
								target="refund-late-delivery-button"
								position="bottom"
							/>
						</>
					)}
				</div>
			</div>
		);
	}
}

export default TrackingDetails;
